import { FunctionComponent, useMemo } from 'react';
import PageContainer from '../productPage/tableComponents/PageContainer';
import { Box, Grid } from '@mui/material';
import SingleDefinitionContainer from './SingleDefinitionContainer';
import VerticalContainer from '../common/VerticalContainer';
import { DetectedDefinition } from '../../schemas/DetectedDefinition';
import { getParameterValuesFromRaw } from '../productPage/ProductHome';
import { ConditionChip } from '../productPage/filterComponents/AppliedFilterInfo';
import { useResponsive } from '../../contexts/ResponsiveContext';
import {
    getDisplayNameForFilterParameter,
    getDisplayNameForReturnParameterValue,
} from '../../utils/StringRelated';
import { ReturnParameterValuesLite } from '../../schemas/ReturnParameterValuesLite';
import { ProductsContent } from '../../graphql/pimcore/modules/siteContent/types/ProductsContent';

interface AllProductsProps {
    definitions: DetectedDefinition[];
    filteredSegments: string[];
    language: string;
    queryRawResults?: ReturnParameterValuesLite;
    searchInput?: string | undefined;
    contentTree: ProductsContent;
}

const notShowBrowseAll = new Set([
    'EMC',
    'Signal Magnetics & Connectors',
    'Antennas, RF & Microwave',
    'Sensors',
    'Circuit Protection',
    'Semiconductors',
    'Piezoelectric Devices',
    'Power Supplies',
    'Electromechanical Devices',
]);

const AllProducts: FunctionComponent<AllProductsProps> = ({
    definitions,
    filteredSegments,
    language,
    queryRawResults,
    searchInput,
    contentTree,
}) => {
    const { single: singlePVFromRaw, multiple: multiplePVFromRaw } =
        getParameterValuesFromRaw(queryRawResults);

    const isRootPage = filteredSegments.length === 1 && filteredSegments[0] === 'Products';

    // Helper function to traverse definitions based on filters
    const getFilteredDefinitions = (
        definitions: DetectedDefinition[],
        filters: string[],
        contentTree: ProductsContent
    ): DetectedDefinition[] => {
        if (filters.length === 0) {
            // No filters applied, return top-level definitions
            return definitions;
        }

        let currentDefinitions = definitions;

        const matchedDefinition = definitions.find((def) => def.name === filters[1]);

        if (matchedDefinition && matchedDefinition.childDefinitions) {
            // 將每個子定義的 parentDefinitionId 更新為父層的 id
            const updatedChildDefinitions = matchedDefinition.childDefinitions.map((child) => ({
                ...child,
                parentDefinitionId: matchedDefinition.id,
            }));

            const allProductView: DetectedDefinition = {
                childDefinitions: [],
                definitionImage: null,
                description: '',
                displayName: `${contentTree.browseAllProducts.browseAllButtonLabel.content}`,
                displayNames: [],
                id: -1,
                name: 'Browse All',
                nameBreadcrumb: '',
                nameBreadcrumbs: [],
                parentDefinitionId: matchedDefinition.id,
                partCount: 0,
                seriesImages: [],
                sortOrder: 0,
            };

            // 如果 displayName 不在 notShowBrowseAll 中，則在 childDefinitions 最前面加入 allProductView
            if (!notShowBrowseAll.has(matchedDefinition.displayName)) {
                return [allProductView, ...updatedChildDefinitions];
            } else {
                return updatedChildDefinitions;
            }
        }

        return currentDefinitions;
    };

    // Memoize the filtered definitions to avoid unnecessary recalculations
    const filteredDefinitions = useMemo(() => {
        return getFilteredDefinitions(definitions, filteredSegments, contentTree);
    }, [definitions, filteredSegments, contentTree]);

    const {
        isMaxScreen,
        isScreenXxl,
        isScreenXl,
        isScreenLg,
        isScreenMd,
        isScreenSm,
        isScreenXs,
        responsiveWidth,
    } = useResponsive();

    // 幫助函式：根據當前螢幕大小決定要佔幾格 (0~12)
    const getGridSize = () => {
        if (isMaxScreen || isScreenXxl || isScreenXl) {
            // 如果是你定義的「超超大螢幕」，就一行放 4 個 => 12 / 4 = 3
            return 3;
        } else if (isScreenLg) {
            // 「大螢幕」 => 一行放 4 個
            return 4;
        } else if (isScreenMd) {
            // 「中螢幕」 => 一行放 2 個 => 12 / 2 = 6
            return 6;
        } else if (isScreenSm) {
            // 「小螢幕」 => 一行放 1 個 => 12 / 1 = 12
            return 12;
        } else if (isScreenXs) {
            // 「最小螢幕」 => 一行放 1 個 => 12 / 1 = 12
            return 12;
        }
        // 預設回傳 12 (通常是 fallback)
        return 12;
    };

    return (
        <PageContainer
            containerName={'all-product-zone'}
            sx={{
                backgroundColor: '#FAFAFA',
                gap: 2,
            }}
            innerSx={{ width: responsiveWidth }}
        >
            {/* Ensure components inside PageContainer are vertically arranged */}
            <VerticalContainer containerName={'all-product-zone'}>
                <Box
                    data-testid={`pv-chips`}
                    sx={{
                        display: 'flex',
                        paddingBottom: '1rem',
                        gap: '1rem',
                        width: '100%',
                        justifyContent: 'flex-start',
                        flexWrap: 'wrap',
                    }}
                >
                    {Array.from(singlePVFromRaw).map(([key, ppv]) => {
                        if (key === '79') {
                            return null;
                        }
                        let label =
                            ppv.parameterValues[0]?.value === true ||
                            ppv.parameterValues[0]?.value === 'true'
                                ? `${getDisplayNameForFilterParameter(ppv, language)}`
                                : `${getDisplayNameForFilterParameter(ppv, language)} = ${getDisplayNameForReturnParameterValue(ppv.parameterValues[0], language)}`;

                        return (
                            <ConditionChip
                                data-testid={`${ppv.parameterName}-value`}
                                key={key}
                                label={label}
                            />
                        );
                    })}

                    {Array.from(multiplePVFromRaw).map(([key, ppv]) => (
                        <ConditionChip
                            data-testid={`${ppv.parameterName}-values`}
                            key={key}
                            label={`${getDisplayNameForFilterParameter(ppv, language)} = Multiple Values`}
                        />
                    ))}
                </Box>

                {/* 使用 MUI Grid 來自動排版，不需手動 chunking */}
                <Grid
                    container
                    spacing={3}
                    sx={{ paddingTop: '2rem', paddingBottom: '2rem', justifyContent: 'flex-start' }}
                >
                    {filteredDefinitions.map((definition) => (
                        <Grid
                            item
                            key={definition.id}
                            xs={getGridSize()}
                            sx={{
                                // 讓 item 變成 flex 容器
                                display: 'flex',
                                justifyContent: 'center',
                            }}
                        >
                            <SingleDefinitionContainer
                                definition={definition}
                                isRootPage={isRootPage}
                                language={language}
                                queryRawResults={queryRawResults}
                                searchInput={searchInput}
                            />
                        </Grid>
                    ))}
                </Grid>
            </VerticalContainer>
        </PageContainer>
    );
};

export default AllProducts;
